/**
 * Football IQ: level table and progression maths.
 *
 * Football IQ is a game mechanic, not a measurement (PRD §26).
 * The copy is deliberately self-deprecating at the low end and
 * teasing at the high end.
 *
 * XP award rules live in ./iqRewards.js so this module stays a pure
 * lookup table that UI can import cheaply.
 */

/** Ordered low → high. `max` is inclusive; the final tier is open-ended. */
export const IQ_LEVELS = Object.freeze([
  {
    id: "no-idea",
    min: 0,
    max: 49,
    label: "I have no idea what's happening",
    short: "Total Rookie",
    icon: "fa-solid fa-face-dizzy",
  },
  {
    id: "starting",
    min: 50,
    max: 99,
    label: "I'm starting to get it",
    short: "Getting It",
    icon: "fa-solid fa-lightbulb",
  },
  {
    id: "casual-fan",
    min: 100,
    max: 199,
    label: "Casual Fan",
    short: "Casual Fan",
    icon: "fa-solid fa-couch",
  },
  {
    id: "football-person",
    min: 200,
    max: 399,
    label: "Football Person",
    short: "Football Person",
    icon: "fa-solid fa-football",
  },
  {
    id: "you-know-football",
    min: 400,
    max: 699,
    label: "Okay, you know football",
    short: "You Know Football",
    icon: "fa-solid fa-star",
  },
  {
    id: "stop-pretending",
    min: 700,
    max: Infinity,
    label: "Stop pretending you don't know football",
    short: "Caught You",
    icon: "fa-solid fa-crown",
  },
]);

/** Coerce anything into a non-negative integer IQ value. */
export function normalizeIQ(value) {
  const n = Number(value);
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.floor(n);
}

/** The level a given IQ falls into. Always returns a level. */
export function getIQLevel(iq) {
  const value = normalizeIQ(iq);
  return (
    IQ_LEVELS.find((level) => value >= level.min && value <= level.max) ??
    IQ_LEVELS[IQ_LEVELS.length - 1]
  );
}

/** The next level up, or null when already at the top tier. */
export function getNextIQLevel(iq) {
  const current = getIQLevel(iq);
  const index = IQ_LEVELS.indexOf(current);
  return index >= 0 && index < IQ_LEVELS.length - 1 ? IQ_LEVELS[index + 1] : null;
}

/**
 * Progress through the *current* level.
 *
 * @returns {{
 *   iq: number,
 *   level: object,
 *   nextLevel: object|null,
 *   percent: number,      // 0-100 through the current band
 *   pointsIntoLevel: number,
 *   pointsToNextLevel: number|null
 * }}
 */
export function getIQProgress(iq) {
  const value = normalizeIQ(iq);
  const level = getIQLevel(value);
  const nextLevel = getNextIQLevel(value);

  if (!nextLevel) {
    return {
      iq: value,
      level,
      nextLevel: null,
      percent: 100,
      pointsIntoLevel: value - level.min,
      pointsToNextLevel: null,
    };
  }

  const bandSize = nextLevel.min - level.min;
  const pointsIntoLevel = value - level.min;
  const percent = bandSize > 0
    ? Math.min(100, Math.max(0, Math.round((pointsIntoLevel / bandSize) * 100)))
    : 100;

  return {
    iq: value,
    level,
    nextLevel,
    percent,
    pointsIntoLevel,
    pointsToNextLevel: nextLevel.min - value,
  };
}
